/* RESPONSIBLE TEAM: team-phone */
import { tracked } from '@glimmer/tracking';
import type { RTCSample } from '@twilio/voice-sdk';
import { post } from 'embercom/lib/ajax';

export type QualityLevel = 'good' | 'fair' | 'poor' | 'muted' | 'no_input' | 'no_audio';

export interface MetricHealth {
  network: QualityLevel;
  microphone: QualityLevel;
  speaker: QualityLevel;
}

export interface DetailedMetrics {
  mos: number;
  jitter: number;
  rtt: number;
  packetLossPercent: number;
  audioInputLevel: number;
  audioOutputLevel: number;
  bitrateSend: number;
  bitrateReceive: number;
  codec: string;
}

export interface CallQualityDeps {
  appId: string;
  adminId: number;
  getConnectionType: () => string | null;
  getEdge: () => string | undefined;
}

const DEGRADE_THRESHOLD = 5;
const RECOVER_THRESHOLD = 5;
const SILENCE_THRESHOLD = 100;
const SILENCE_DURATION_THRESHOLD = 15;
const MOS_POOR_THRESHOLD = 2.5;
const MOS_FAIR_THRESHOLD = 3.5;

export default class CallQualityMonitor {
  // Real-time UI state
  @tracked overallQuality: QualityLevel = 'good';
  @tracked metricHealth: MetricHealth = { network: 'good', microphone: 'good', speaker: 'good' };
  @tracked isMicrophoneMuted = false;
  @tracked currentMetrics: DetailedMetrics = {
    mos: 0,
    jitter: 0,
    rtt: 0,
    packetLossPercent: 0,
    audioInputLevel: 0,
    audioOutputLevel: 0,
    bitrateSend: 0,
    bitrateReceive: 0,
    codec: '',
  };
  @tracked hasIssueDetected = false;

  get indicatorQuality(): QualityLevel {
    let { network, microphone, speaker } = this.metricHealth;
    let levels: QualityLevel[] = [network];
    if (microphone !== 'good' && microphone !== 'muted') {
      levels.push('poor');
    }
    if (speaker !== 'good') {
      levels.push('poor');
    }
    return this.worst(...levels);
  }

  // Real-time classification state
  private consecutiveGoodSamples = 0;
  private consecutiveBadSamples = 0;
  private consecutiveSilentInput = 0;
  private consecutiveSilentOutput = 0;
  private previousBytesSent = 0;
  private previousBytesReceived = 0;
  private previousTimestamp = 0;
  private hasPreviousBytes = false;

  // Diagnostics state (aggregated on disconnect)
  private deps: CallQualityDeps;
  private samples: RTCSample[] = [];
  private warnings: Array<{ name: string; timestamp: number }> = [];
  private errors: Array<{ code: number; message: string; timestamp: number }> = [];
  private callStartAt?: number;
  private codecHistory: string[] = [];
  private callSid?: string;
  private direction?: 'inbound' | 'outbound';
  private conversationId?: number;

  constructor(deps: CallQualityDeps) {
    this.deps = deps;
  }

  setMicrophoneMuted(muted: boolean): void {
    this.isMicrophoneMuted = muted;
    if (muted) {
      this.consecutiveSilentInput = 0;
    }
  }

  startTracking(params: {
    direction: 'inbound' | 'outbound';
    callSid?: string;
    conversationId?: number;
  }): void {
    this.direction = params.direction;
    this.callSid = params.callSid;
    this.conversationId = params.conversationId;
  }

  onAccept(callSid?: string): void {
    this.callStartAt = Date.now();
    this.callSid = callSid;
  }

  onSample(sample: RTCSample): void {
    // Diagnostics: collect for disconnect-time aggregation
    this.samples.push(sample);
    let codec = sample.codecName;
    if (codec && this.codecHistory[this.codecHistory.length - 1] !== codec) {
      this.codecHistory.push(codec);
    }

    // Real-time: update UI state
    this.updateCurrentMetrics(sample);
    this.updateSilenceCounters(sample);

    let micQuality = this.classifyMicrophone();
    let speakerQuality = this.classifySpeaker();
    let instantNetworkQuality = this.classifyNetworkSample(sample);

    this.metricHealth = {
      network: instantNetworkQuality,
      microphone: micQuality,
      speaker: speakerQuality,
    };

    this.applyHysteresis(instantNetworkQuality);
  }

  onWarning(warningName: string): void {
    this.warnings.push({ name: warningName, timestamp: Date.now() });
  }

  onError(error: { code: number; message: string }): void {
    this.errors.push({ code: error.code, message: error.message, timestamp: Date.now() });
  }

  onDisconnect(): void {
    this.sendDiagnostics();
  }

  reset(): void {
    // Real-time state
    this.overallQuality = 'good';
    this.metricHealth = { network: 'good', microphone: 'good', speaker: 'good' };
    this.currentMetrics = {
      mos: 0,
      jitter: 0,
      rtt: 0,
      packetLossPercent: 0,
      audioInputLevel: 0,
      audioOutputLevel: 0,
      bitrateSend: 0,
      bitrateReceive: 0,
      codec: '',
    };
    this.hasIssueDetected = false;
    this.isMicrophoneMuted = false;
    this.consecutiveGoodSamples = 0;
    this.consecutiveBadSamples = 0;
    this.consecutiveSilentInput = 0;
    this.consecutiveSilentOutput = 0;
    this.previousBytesSent = 0;
    this.previousBytesReceived = 0;
    this.previousTimestamp = 0;
    this.hasPreviousBytes = false;

    // Diagnostics state
    this.samples = [];
    this.warnings = [];
    this.errors = [];
    this.callStartAt = undefined;
    this.codecHistory = [];
    this.callSid = undefined;
    this.direction = undefined;
    this.conversationId = undefined;
  }

  // --- Real-time classification (drives UI) ---

  private updateCurrentMetrics(sample: RTCSample): void {
    let bitrateSend = 0;
    let bitrateReceive = 0;

    if (this.hasPreviousBytes) {
      let elapsedSeconds = (sample.timestamp - this.previousTimestamp) / 1000;
      if (elapsedSeconds > 0) {
        bitrateSend =
          Math.max(0, (sample.totals.bytesSent - this.previousBytesSent) * 8) /
          (elapsedSeconds * 1000);
        bitrateReceive =
          Math.max(0, (sample.totals.bytesReceived - this.previousBytesReceived) * 8) /
          (elapsedSeconds * 1000);
      }
    }

    this.previousBytesSent = sample.totals.bytesSent;
    this.previousBytesReceived = sample.totals.bytesReceived;
    this.previousTimestamp = sample.timestamp;
    this.hasPreviousBytes = true;

    this.currentMetrics = {
      mos: this.round2(sample.mos ?? 0),
      jitter: Math.round(sample.jitter * 100) / 100,
      rtt: Math.round(sample.rtt * 100) / 100,
      packetLossPercent: Math.round(sample.packetsLostFraction * 10000) / 100,
      audioInputLevel: sample.audioInputLevel,
      audioOutputLevel: sample.audioOutputLevel,
      bitrateSend: Math.round(bitrateSend * 10) / 10,
      bitrateReceive: Math.round(bitrateReceive * 10) / 10,
      codec: sample.codecName ?? '',
    };
  }

  private updateSilenceCounters(sample: RTCSample): void {
    if (this.isMicrophoneMuted) {
      this.consecutiveSilentInput = 0;
    } else if (sample.audioInputLevel < SILENCE_THRESHOLD) {
      this.consecutiveSilentInput++;
    } else {
      this.consecutiveSilentInput = 0;
    }

    if (sample.audioOutputLevel < SILENCE_THRESHOLD) {
      this.consecutiveSilentOutput++;
    } else {
      this.consecutiveSilentOutput = 0;
    }
  }

  private classifyNetworkSample(sample: RTCSample): QualityLevel {
    let rttLevel = this.classifyRtt(sample.rtt);
    let jitterLevel = this.classifyJitter(sample.jitter);
    let packetLossLevel = this.classifyPacketLoss(sample.packetsLostFraction);

    let mosLevel: QualityLevel = 'good';
    if (sample.mos !== null && sample.mos !== undefined) {
      if (sample.mos < MOS_POOR_THRESHOLD) {
        mosLevel = 'poor';
      } else if (sample.mos < MOS_FAIR_THRESHOLD) {
        mosLevel = 'fair';
      }
    }

    return this.worst(rttLevel, jitterLevel, packetLossLevel, mosLevel);
  }

  private classifyMicrophone(): QualityLevel {
    if (this.isMicrophoneMuted) {
      return 'muted';
    }
    if (this.consecutiveSilentInput >= SILENCE_DURATION_THRESHOLD) {
      return 'no_input';
    }
    return 'good';
  }

  private classifySpeaker(): QualityLevel {
    if (this.consecutiveSilentOutput >= SILENCE_DURATION_THRESHOLD) {
      return 'no_audio';
    }
    return 'good';
  }

  private classifyRtt(value: number): QualityLevel {
    if (value > 400) {
      return 'poor';
    }
    if (value > 200) {
      return 'fair';
    }
    return 'good';
  }

  private classifyJitter(value: number): QualityLevel {
    if (value > 80) {
      return 'poor';
    }
    if (value > 30) {
      return 'fair';
    }
    return 'good';
  }

  private classifyPacketLoss(fraction: number): QualityLevel {
    let percent = fraction * 100;
    if (percent > 5) {
      return 'poor';
    }
    if (percent > 1) {
      return 'fair';
    }
    return 'good';
  }

  private worst(...levels: QualityLevel[]): QualityLevel {
    if (levels.includes('poor')) {
      return 'poor';
    }
    if (levels.includes('fair')) {
      return 'fair';
    }
    return 'good';
  }

  private applyHysteresis(networkQuality: QualityLevel): void {
    let isBad = networkQuality === 'poor';

    if (isBad) {
      this.consecutiveBadSamples++;
      this.consecutiveGoodSamples = 0;
    } else {
      this.consecutiveGoodSamples++;
      this.consecutiveBadSamples = 0;
    }

    if (this.overallQuality === 'good') {
      if (this.consecutiveBadSamples >= DEGRADE_THRESHOLD) {
        this.overallQuality = networkQuality;
        this.hasIssueDetected = true;
      }
    } else if (this.consecutiveGoodSamples >= RECOVER_THRESHOLD) {
      this.overallQuality = 'good';
      this.hasIssueDetected = false;
    } else if (this.consecutiveBadSamples >= DEGRADE_THRESHOLD) {
      this.overallQuality = networkQuality;
    }
  }

  // --- Diagnostics (aggregated and sent on disconnect) ---

  private sendDiagnostics(): void {
    try {
      let samples = this.samples;
      if (samples.length === 0) {
        return;
      }

      let payload = this.aggregateSamples(samples);

      post('/ember/twilio_call_quality_diagnostics', {
        app_id: this.deps.appId,
        admin_id: this.deps.adminId,
        ...payload,
      }).catch((_e: unknown) => {
        // Never block disconnect flow
      });
    } catch (e: unknown) {
      // Never block disconnect flow
    } finally {
      this.samples = [];
      this.warnings = [];
      this.errors = [];
      this.callStartAt = undefined;
      this.codecHistory = [];
      this.callSid = undefined;
      this.direction = undefined;
      this.conversationId = undefined;
    }
  }

  private aggregateSamples(samples: RTCSample[]): Record<string, any> {
    let totalSamples = samples.length;
    let durationSeconds = this.callStartAt
      ? Math.round((Date.now() - this.callStartAt) / 1000)
      : totalSamples;

    let mosValues = samples.map((s) => s.mos).filter((m): m is number => m !== null);
    let mosAvg = mosValues.length > 0 ? mosValues.reduce((a, b) => a + b, 0) / mosValues.length : 0;
    let mosMin = mosValues.length > 0 ? Math.min(...mosValues) : 0;
    let mosMax = mosValues.length > 0 ? Math.max(...mosValues) : 0;
    let mosStdev = this.calculateStdev(mosValues, mosAvg);
    let mosSecondsBelow3 = mosValues.filter((m) => m < 3.0).length;
    let mosSecondsBelow2 = mosValues.filter((m) => m < 2.0).length;
    let mosPercentBelow3 =
      mosValues.length > 0 ? this.round2((mosSecondsBelow3 / mosValues.length) * 100) : 0;

    let jitterValues = samples.map((s) => s.jitter);
    let jitterAvg = jitterValues.reduce((a, b) => a + b, 0) / jitterValues.length;
    let jitterMax = Math.max(...jitterValues);
    let jitterP95 = this.percentile(jitterValues, 95);

    let rttValues = samples.map((s) => s.rtt);
    let rttAvg = rttValues.reduce((a, b) => a + b, 0) / rttValues.length;
    let rttMax = Math.max(...rttValues);
    let rttP95 = this.percentile(rttValues, 95);

    let lastSample = samples[samples.length - 1];
    let packetsSent = lastSample.totals.packetsSent;
    let packetsLost = lastSample.totals.packetsLost;
    let packetsReceived = lastSample.totals.packetsReceived;
    let packetsLostReceived = lastSample.totals.packetsLostFraction;
    let packetLossPercent = packetsSent > 0 ? this.round4((packetsLost / packetsSent) * 100) : 0;
    let lossFractions = samples.map((s) => s.packetsLostFraction);
    let packetLossMaxFraction = Math.max(...lossFractions);
    let packetLossAvgFraction = lossFractions.reduce((a, b) => a + b, 0) / lossFractions.length;

    let inputLevels = samples.map((s) => s.audioInputLevel);
    let outputLevels = samples.map((s) => s.audioOutputLevel);
    let audioInputLevelAvg = Math.round(
      inputLevels.reduce((a, b) => a + b, 0) / inputLevels.length,
    );
    let audioOutputLevelAvg = Math.round(
      outputLevels.reduce((a, b) => a + b, 0) / outputLevels.length,
    );

    let silentInputSeconds = inputLevels.filter((l) => l < 100).length;
    let silentInputPercent =
      totalSamples > 0 ? this.round2((silentInputSeconds / totalSamples) * 100) : 0;
    let silentOutputSeconds = outputLevels.filter((l) => l < 100).length;
    let silentOutputPercent =
      totalSamples > 0 ? this.round2((silentOutputSeconds / totalSamples) * 100) : 0;

    let bytesSent = lastSample.totals.bytesSent;
    let bytesReceived = lastSample.totals.bytesReceived;

    let oneWayAudioSuspectedSeconds = 0;
    for (let i = 1; i < samples.length; i++) {
      let prevBytesSent = samples[i - 1].totals.bytesSent;
      let currBytesSent = samples[i].totals.bytesSent;
      let hasInput = samples[i].audioInputLevel > 100;
      let bytesStalled = currBytesSent === prevBytesSent;
      if (hasInput && bytesStalled) {
        oneWayAudioSuspectedSeconds++;
      }
    }

    let codec = lastSample.codecName;
    let codecChanged = this.codecHistory.length > 1;

    let degradationPeriods = this.findDegradationPeriods(mosValues);

    let worstSamples = [...samples]
      .filter((s) => s.mos !== null)
      .sort((a, b) => (a.mos ?? 5) - (b.mos ?? 5))
      .slice(0, 5)
      .map((s, _i) => ({
        second: samples.indexOf(s),
        mos: s.mos,
        jitter: this.round2(s.jitter),
        rtt: this.round2(s.rtt),
        packet_loss_fraction: this.round4(s.packetsLostFraction),
        audio_input_level: s.audioInputLevel,
        audio_output_level: s.audioOutputLevel,
      }));

    let warnings = this.warnings.slice(0, 50);

    return {
      call_sid: this.callSid,
      direction: this.direction,
      conversation_id: this.conversationId,
      duration_seconds: durationSeconds,
      total_samples: totalSamples,
      mos_average: this.round2(mosAvg),
      mos_min: this.round2(mosMin),
      mos_max: this.round2(mosMax),
      mos_stdev: this.round2(mosStdev),
      mos_seconds_below_3: mosSecondsBelow3,
      mos_seconds_below_2: mosSecondsBelow2,
      mos_percent_below_3: mosPercentBelow3,
      jitter_avg_ms: this.round2(jitterAvg),
      jitter_max_ms: this.round2(jitterMax),
      jitter_p95_ms: this.round2(jitterP95),
      rtt_avg_ms: this.round2(rttAvg),
      rtt_max_ms: this.round2(rttMax),
      rtt_p95_ms: this.round2(rttP95),
      packets_sent: packetsSent,
      packets_lost: packetsLost,
      packets_received: packetsReceived,
      packets_lost_received: packetsLostReceived,
      packet_loss_percent: packetLossPercent,
      packet_loss_max_fraction: this.round4(packetLossMaxFraction),
      packet_loss_avg_fraction: this.round4(packetLossAvgFraction),
      audio_input_level_avg: audioInputLevelAvg,
      audio_output_level_avg: audioOutputLevelAvg,
      silent_input_seconds: silentInputSeconds,
      silent_input_percent: silentInputPercent,
      silent_output_seconds: silentOutputSeconds,
      silent_output_percent: silentOutputPercent,
      bytes_sent: bytesSent,
      bytes_received: bytesReceived,
      codec,
      codec_changed: codecChanged,
      codec_history: this.codecHistory,
      one_way_audio_suspected_seconds: oneWayAudioSuspectedSeconds,
      network_online: navigator.onLine,
      connection_type: this.deps.getConnectionType(),
      twilio_edge: this.deps.getEdge(),
      tab_hidden: document.hidden,
      degradation_periods: degradationPeriods.length > 0 ? degradationPeriods : undefined,
      worst_samples: worstSamples.length > 0 ? worstSamples : undefined,
      warnings: warnings.length > 0 ? warnings : undefined,
      warning_count: this.warnings.length,
      errors: this.errors.length > 0 ? this.errors.slice(0, 10) : undefined,
      error_count: this.errors.length,
    };
  }

  private percentile(values: number[], p: number): number {
    let sorted = [...values].sort((a, b) => a - b);
    let index = Math.ceil((p / 100) * sorted.length) - 1;
    return sorted[Math.max(0, index)];
  }

  private calculateStdev(values: number[], mean: number): number {
    if (values.length < 2) {
      return 0;
    }
    let squaredDiffs = values.map((v) => (v - mean) ** 2);
    let avgSquaredDiff = squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
    return Math.sqrt(avgSquaredDiff);
  }

  private round2(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private round4(value: number): number {
    return Math.round(value * 10000) / 10000;
  }

  private findDegradationPeriods(mosValues: number[]): Array<Record<string, number>> {
    let periods: Array<{ startSecond: number; endSecond: number; values: number[] }> = [];
    let currentPeriod: { startSecond: number; values: number[] } | null = null;

    mosValues.forEach((mos, i) => {
      if (mos < 3.0) {
        if (!currentPeriod) {
          currentPeriod = { startSecond: i, values: [mos] };
        } else {
          currentPeriod.values.push(mos);
        }
      } else {
        if (currentPeriod && currentPeriod.values.length >= 3) {
          periods.push({
            startSecond: currentPeriod.startSecond,
            endSecond: i - 1,
            values: currentPeriod.values,
          });
        }
        currentPeriod = null;
      }
    });

    if (currentPeriod) {
      let cp = currentPeriod as { startSecond: number; values: number[] };
      if (cp.values.length >= 3) {
        periods.push({
          startSecond: cp.startSecond,
          endSecond: mosValues.length - 1,
          values: cp.values,
        });
      }
    }

    return periods
      .sort((a, b) => b.values.length - a.values.length)
      .slice(0, 5)
      .map((p) => this.summarizePeriod(p));
  }

  private summarizePeriod(period: {
    startSecond: number;
    endSecond: number;
    values: number[];
  }): Record<string, number> {
    let avg = period.values.reduce((a, b) => a + b, 0) / period.values.length;
    return {
      start_second: period.startSecond,
      end_second: period.endSecond,
      duration_seconds: period.values.length,
      avg_mos: this.round2(avg),
    };
  }
}
